import { LitElement, html, property } from 'lit-element'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import './style.scss'

/**
 * Checkbox lets consumers render a custom checkbox input that meets
 * accessibility standards and lets focus-visible highlight surround the both
 * the checkbox and its label. Consumers may listen for `change` events as they
 * normally would against a `<input type='checkbox'>` element and set/get its
 * `checked`, `indeterminate`, `disabled` or `value` properties.
 *
 * Render the checkbox in a checked state.
 * `<pe-checkbox checked></pe-checkbox>`
 *
 * Render the checkbox in an indeterminate state.
 * `<pe-checkbox indeterminate></pe-checkbox>`
 *
 * Render the checkbox in an indeterminate and checked state. This changes what
 * happens when a user clicks on the checkbox. Instead of going from
 * indeterminate to a checked state, it will go from indeterminate to an
 * unchecked state instead.
 * `<pe-checkbox indeterminate checked></pe-checkbox>`
 *
 * Render a labeled checkbox by supplying inner text content.
 * `<pe-checkbox>Enable XYZ</pe-checkbox>`
 *
 * @element pe-checkbox
 *
 * @prop {boolean} checked=false - Controls whether checkbox is `checked`.
 * @prop {boolean} disabled=false - When disabled, the checkbox cannot be
 *  interacted with and renders in muted colors.
 * @prop {boolean} focus-indicator=false - Enables a subtle focus indicator
 *  around the checkbox on active/hover states.
 * @prop {boolean} indeterminate=false - Checkbox is indeterminate when it is
 *  neither checked or unchecked but somewhere in between. This may be useful
 *  when a checkbox is the parent of child checkboxes and some but not all
 *  children are checked.
 * @prop {string} label - Returns the text label next to the checkbox.
 * @prop {number} tabIndex=0 - Set the tabindex attr for the label.
 * @prop {string} value - Get or set the checkbox value, which is typically
 *  included if the checkbox is submitted in a form.
 *
 * @slot - Unnamed slot is used as the checkbox label.
 *
 * @cssprop --pe-checkbox-mark-color=$pe-pure-white - Color of the checked or
 *  indeterminate mark within the checkbox when enabled or disabled.
 * @cssprop --pe-checkbox-size=1.25rem - Sets width and height of the checkbox.
 * @cssprop --pe-checkbox-theme-color=$pe-blue-500 - Color of the checkbox in a
 *  "checked" state.
 * @cssprop --pe-checkbox-unchecked-color=$pe-neutral-600 - Color of the
 *  checkbox in an "unchecked" state.
 */
export default class CheckboxElement extends LitElement {
  static get tagName () { return 'pe-checkbox' }

  @property({ type: Boolean, reflect: true })
  checked = false

  @property({ type: Boolean, reflect: true })
  disabled = false

  @property({ type: Boolean, reflect: true, attribute: 'focus-indicator' })
  focusIndicator = false

  @property({ type: Boolean, reflect: true })
  indeterminate = false

  @property({ type: Number })
  tabIndex = 0

  @property({ type: String, reflect: true })
  value

  get label () {
    return this.slots().map(node => node.textContent.trim()).join('')
  }

  constructor () {
    super()
    this.addEventListener('click', handleContainerClick)
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  createRenderRoot () { return this }

  render () {
    return html`
    <label class=${bem('label')}
        role='checkbox'
        aria-checked=${this.indeterminate ? 'mixed' : this.checked}
        aria-label=${this.label || 'Select'}
        tabindex=${this.disabled ? -1 : this.tabIndex}
        @keypress=${(e) => e.preventDefault()}
        @keyup=${handleKeyup}
        @change=${handleChange}>
      <input class=${bem('input')}
          type='checkbox'
          tabindex='-1'
          .checked=${this.checked}
          .disabled=${this.disabled}
          .indeterminate=${this.indeterminate}
          .value=${this.value} />
      <div class=${bem('checkbox')}>
        ${renderMarks.call(this)}
      </div>
      ${renderSlot.call(this)}
    </label>`
  }
}

function renderSlot () {
  if (!this.label) return
  return html`<div class=${bem('slot')}>${this.slots()}</div>`
}

function renderMarks () {
  return html`
    <svg class=${bem('checkmark')} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 12 10"><path stroke-linecap="round" stroke-linejoin="round" d="M1 5.75l2.75 3L11 1.5"/></svg>
    <svg class=${bem('indeterminate')} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 2"><path stroke-linecap="round" stroke-linejoin="round" d="M1 1h8"/></svg>
  `
}

function handleContainerClick (e) {
  // Propagate clicks from this element's outer container into the chekbox.
  // This covers the case where consumers add padding and users click
  // somewhere inside the padded area.
  if (e.target !== this) return
  this.querySelector('input[type=checkbox]').click()
}

function handleChange (e) {
  this.checked = e.target.checked
  this.indeterminate = e.target.indeterminate
}

function handleKeyup (e) {
  const VALID_KEYS = [' ']
  if (!VALID_KEYS.includes(e.key)) { return }

  this.querySelector('input[type=checkbox]').click()
  e.preventDefault()
}

const bem = bemBlock(CheckboxElement.tagName)
register(module, CheckboxElement.tagName, CheckboxElement)
